// ════════════════════════════════════════════════════════════════════════════
//  Udisys Gestor Web  —  pages/Configuracoes/ConfiguracoesFiscais.tsx
//  Documentos Fiscais — emissão de NF-e, NFC-e, NFS-e, CT-e, CT-e OS, MDF-e
//  e certificado digital.
// ════════════════════════════════════════════════════════════════════════════

import { useEffect, useState } from 'react';
import {
  Save, ShieldCheck, FileText, RefreshCw, Receipt, Truck, FileBarChart, Construction, Info, Percent,
  FileKey, AlertTriangle, Landmark,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Skeleton } from '@/components/ui/skeleton';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { useAuth } from '@/hooks/useAuth';
import {
  useConfigNfe, useConfigNfce, useConfigMdfe, useConfigCte, useConfigCteOs, useConfigCertificado,
  useConfigReforma,
} from '@/features/configuracoes/hooks/useConfiguracoes';
import type {
  AtualizarConfigNfeRequest, AtualizarConfigNfceRequest, AtualizarConfigMdfeRequest,
  AtualizarConfigCteRequest, AtualizarConfigCteOsRequest,
  AtualizarConfigCertificadoRequest, AtualizarConfigReformaRequest,
} from '@/features/configuracoes/types';
import { useCClassTribCombo } from '@/features/fiscal/hooks/useFiscal';
import { NfseConfigForm } from './ConfiguracaoNfse';
import { EmitenteFiscalTab } from './EmitenteFiscalTab';
import IbptTab from './IbptTab';

const TIPO_CERTIFICADO_LABELS: Record<number, string> = {
  1: 'A1 — Repositório Windows',
  2: 'A1 — Arquivo PFX',
  3: 'A3 — Token / Smart-Card',
  4: 'A1 — ByteArray',
};

// ══════════════════════════════════════════════════════════════════════════════
//  Aba NF-e / NFC-e (compartilham a mesma configuração — modeloDocumento decide)
// ══════════════════════════════════════════════════════════════════════════════

function TabNfe({ empresaId, modeloFixo }: { empresaId: number; modeloFixo?: 55 | 65 }) {
  const { data, isLoading, salvar, isSalvando } = useConfigNfe(empresaId);

  const [form, setForm] = useState<AtualizarConfigNfeRequest>({
    numNota: 1,
    serie: '1',
    versaoLayout: '4.00',
    tipoAmbiente: 2,
    tipoEmissao: 1,
    ibSincrono: false,
    timeout: 30,
    modeloDocumento: modeloFixo ?? 55,
    diretorioBackupXml: null,
    diretorioBackupPdf: null,
    diretorioRecibo: null,
    diretorioSchemas: null,
    casasDecimaisValorUnit: 2,
    casasDecimaisQtde: 2,
    ibCalcularProdutoCst90: false,
    ibAgruparProdutos: false,
    emissaoNormal: 'UmaLinha',
    emissaoContingencia: 'UmaLinha',
    tipoImpressaoUnidadeValor: 0,
  });

  useEffect(() => {
    if (!data) return;
    setForm({
      numNota: data.numNota,
      serie: data.serie,
      versaoLayout: data.versaoLayout ?? '4.00',
      tipoAmbiente: data.tipoAmbiente,
      tipoEmissao: data.tipoEmissao,
      ibSincrono: data.ibSincrono,
      timeout: data.timeout,
      modeloDocumento: modeloFixo ?? data.modeloDocumento,
      diretorioBackupXml: data.diretorioBackupXml,
      diretorioBackupPdf: data.diretorioBackupPdf,
      diretorioRecibo: data.diretorioRecibo,
      diretorioSchemas: data.diretorioSchemas,
      casasDecimaisValorUnit: data.casasDecimaisValorUnit,
      casasDecimaisQtde: data.casasDecimaisQtde,
      ibCalcularProdutoCst90: data.ibCalcularProdutoCst90,
      ibAgruparProdutos: data.ibAgruparProdutos,
      emissaoNormal: data.emissaoNormal,
      emissaoContingencia: data.emissaoContingencia,
      tipoImpressaoUnidadeValor: data.tipoImpressaoUnidadeValor,
    });
  }, [data, modeloFixo]);

  const set = <K extends keyof AtualizarConfigNfeRequest>(k: K, v: AtualizarConfigNfeRequest[K]) =>
    setForm((f) => ({ ...f, [k]: v }));

  if (isLoading)
    return (
      <div className="space-y-4 pt-4">
        {Array.from({ length: 6 }).map((_, i) => (
          <Skeleton key={i} className="h-9 w-full" />
        ))}
      </div>
    );

  return (
    <div className="space-y-6 pt-4">
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Ambiente e Emissão</CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-1">
            <Label>Ambiente</Label>
            <Select
              value={String(form.tipoAmbiente)}
              onValueChange={(v) => set('tipoAmbiente', Number(v))}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="0">
                  <span className="flex items-center gap-2">
                    Homologação
                    <Badge className="bg-yellow-500/10 text-yellow-600 text-xs">Testes</Badge>
                  </span>
                </SelectItem>
                <SelectItem value="1">
                  <span className="flex items-center gap-2">
                    Produção
                    <Badge className="bg-green-500/10 text-green-600 text-xs">Fiscal real</Badge>
                  </span>
                </SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1">
            <Label>Tipo de Emissão</Label>
            <Select
              value={String(form.tipoEmissao)}
              onValueChange={(v) => set('tipoEmissao', Number(v))}
            >
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="0">Normal (online)</SelectItem>
                <SelectItem value="1">Contingência (offline)</SelectItem>
                <SelectItem value="2">SCAN</SelectItem>
                <SelectItem value="3">DPEC</SelectItem>
                <SelectItem value="4">EPEC</SelectItem>
                <SelectItem value="5">FS-DA</SelectItem>
                <SelectItem value="6">SVC-AN</SelectItem>
                <SelectItem value="7">SVC-RS</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {!modeloFixo && (
            <div className="space-y-1">
              <Label>Modelo do Documento</Label>
              <Select
                value={String(form.modeloDocumento)}
                onValueChange={(v) => set('modeloDocumento', Number(v))}
              >
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="55">55 — NF-e</SelectItem>
                  <SelectItem value="65">65 — NFC-e</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}

          <div className="space-y-1">
            <Label>Versão do Layout</Label>
            <Input
              value={form.versaoLayout ?? ''}
              onChange={(e) => set('versaoLayout', e.target.value || null)}
              placeholder="4.00"
            />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Numeração</CardTitle>
          <CardDescription>Próximo número de nota e série padrão.</CardDescription>
        </CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="space-y-1">
            <Label>Próximo Número</Label>
            <Input
              type="number"
              min={1}
              value={form.numNota}
              onChange={(e) => set('numNota', Number(e.target.value))}
            />
          </div>
          <div className="space-y-1">
            <Label>Série</Label>
            <Input
              value={form.serie}
              onChange={(e) => set('serie', e.target.value)}
              maxLength={5}
            />
          </div>
          <div className="space-y-1">
            <Label>Timeout (segundos)</Label>
            <Input
              type="number"
              min={5}
              max={600}
              value={form.timeout}
              onChange={(e) => set('timeout', Number(e.target.value))}
            />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Casas Decimais</CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-1">
            <Label>Valor Unitário</Label>
            <Select
              value={String(form.casasDecimaisValorUnit)}
              onValueChange={(v) => set('casasDecimaisValorUnit', Number(v))}
            >
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {[2, 3, 4, 5].map((n) => (
                  <SelectItem key={n} value={String(n)}>{n} casas</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label>Quantidade</Label>
            <Select
              value={String(form.casasDecimaisQtde)}
              onValueChange={(v) => set('casasDecimaisQtde', Number(v))}
            >
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {[2, 3, 4].map((n) => (
                  <SelectItem key={n} value={String(n)}>{n} casas</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Opções de Emissão</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium">Emissão síncrona</p>
              <p className="text-xs text-muted-foreground">Aguarda retorno da SEFAZ na mesma requisição.</p>
            </div>
            <Switch
              checked={form.ibSincrono}
              onCheckedChange={(v) => set('ibSincrono', v)}
            />
          </div>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium">Calcular produto CST 90</p>
              <p className="text-xs text-muted-foreground">Aplica tributação especial para CST 90 nos produtos.</p>
            </div>
            <Switch
              checked={form.ibCalcularProdutoCst90}
              onCheckedChange={(v) => set('ibCalcularProdutoCst90', v)}
            />
          </div>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium">Agrupar produtos iguais</p>
              <p className="text-xs text-muted-foreground">Consolida itens duplicados em uma única linha na NF-e.</p>
            </div>
            <Switch
              checked={form.ibAgruparProdutos}
              onCheckedChange={(v) => set('ibAgruparProdutos', v)}
            />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Impressão DANFE</CardTitle>
          <CardDescription>Layout da impressão térmica/A4 e formato dos itens.</CardDescription>
        </CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="space-y-1">
            <Label>Emissão Normal</Label>
            <Select
              value={form.emissaoNormal ?? 'UmaLinha'}
              onValueChange={(v) => set('emissaoNormal', v)}
            >
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="UmaLinha">Uma linha por item</SelectItem>
                <SelectItem value="DuasLinhas">Duas linhas por item</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label>Emissão Contingência</Label>
            <Select
              value={form.emissaoContingencia ?? 'UmaLinha'}
              onValueChange={(v) => set('emissaoContingencia', v)}
            >
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="UmaLinha">Uma linha por item</SelectItem>
                <SelectItem value="DuasLinhas">Duas linhas por item</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label>Tipo de Impressão (Un./Valor)</Label>
            <Select
              value={String(form.tipoImpressaoUnidadeValor)}
              onValueChange={(v) => set('tipoImpressaoUnidadeValor', Number(v))}
            >
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="0">Padrão</SelectItem>
                <SelectItem value="1">Compacto</SelectItem>
                <SelectItem value="2">Expandido</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Diretórios de Backup</CardTitle>
          <CardDescription>Caminhos locais para armazenar XMLs, PDFs, recibos e schemas.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="space-y-1">
            <Label>Backup XML</Label>
            <Input
              value={form.diretorioBackupXml ?? ''}
              onChange={(e) => set('diretorioBackupXml', e.target.value || null)}
              placeholder="C:\NFe\XML"
            />
          </div>
          <div className="space-y-1">
            <Label>Backup PDF (DANFE)</Label>
            <Input
              value={form.diretorioBackupPdf ?? ''}
              onChange={(e) => set('diretorioBackupPdf', e.target.value || null)}
              placeholder="C:\NFe\PDF"
            />
          </div>
          <div className="space-y-1">
            <Label>Recibos</Label>
            <Input
              value={form.diretorioRecibo ?? ''}
              onChange={(e) => set('diretorioRecibo', e.target.value || null)}
              placeholder="C:\NFe\Recibos"
            />
          </div>
          <div className="space-y-1">
            <Label>Schemas XSD</Label>
            <Input
              value={form.diretorioSchemas ?? ''}
              onChange={(e) => set('diretorioSchemas', e.target.value || null)}
              placeholder="C:\NFe\Schemas"
            />
          </div>
        </CardContent>
      </Card>

      <div className="flex justify-end">
        <Button onClick={() => salvar(form)} disabled={isSalvando} className="gap-2 w-full sm:w-auto">
          {isSalvando
            ? <><RefreshCw className="h-4 w-4 animate-spin" />Salvando...</>
            : <><Save className="h-4 w-4" />Salvar configuração{modeloFixo === 65 ? ' NFC-e' : modeloFixo === 55 ? ' NF-e' : ''}</>}
        </Button>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
//  Aba NFC-e (modelo 65 — CSC + QR Code + fallback automático)
// ══════════════════════════════════════════════════════════════════════════════

function TabNfce({ empresaId }: { empresaId: number }) {
  const { data, isLoading, salvar, isSalvando } = useConfigNfce(empresaId);

  const [form, setForm] = useState<AtualizarConfigNfceRequest>({
    numNfce: 1,
    serieNfce: '1',
    tipoAmbiente: 2,
    tipoEmissao: 1,
    versaoLayout: '4.00',
    timeout: 30,
    identificadorCsc: null,
    csc: null,
    versaoQrCode: '2.0',
    posicaoQrCode: 'Abaixo',
    diretorioBackupXml: null,
    diretorioBackupPdf: null,
    diretorioRecibo: null,
    diretorioSchemas: '\\schemas\\NFe',
    emissaoNormal: 'UmaLinha',
    emissaoContingencia: 'UmaLinha',
    ibImprimirDuasVias: false,
    ibFallbackAutomatico: false,
    timeoutDeteccaoMs: 5000,
    cacheFalhaSegundos: 30,
  });

  useEffect(() => {
    if (!data) return;
    setForm({
      numNfce: data.numNfce,
      serieNfce: data.serieNfce,
      tipoAmbiente: data.tipoAmbiente,
      tipoEmissao: data.tipoEmissao,
      versaoLayout: data.versaoLayout,
      timeout: data.timeout,
      identificadorCsc: data.identificadorCsc,
      csc: data.csc,
      versaoQrCode: data.versaoQrCode,
      posicaoQrCode: data.posicaoQrCode,
      diretorioBackupXml: data.diretorioBackupXml,
      diretorioBackupPdf: data.diretorioBackupPdf,
      diretorioRecibo: data.diretorioRecibo,
      diretorioSchemas: data.diretorioSchemas,
      emissaoNormal: data.emissaoNormal,
      emissaoContingencia: data.emissaoContingencia,
      ibImprimirDuasVias: data.ibImprimirDuasVias,
      ibFallbackAutomatico: data.ibFallbackAutomatico,
      timeoutDeteccaoMs: data.timeoutDeteccaoMs,
      cacheFalhaSegundos: data.cacheFalhaSegundos,
    });
  }, [data]);

  const set = <K extends keyof AtualizarConfigNfceRequest>(k: K, v: AtualizarConfigNfceRequest[K]) =>
    setForm((f) => ({ ...f, [k]: v }));

  if (isLoading)
    return (
      <div className="space-y-4 pt-4">
        {Array.from({ length: 8 }).map((_, i) => <Skeleton key={i} className="h-9 w-full" />)}
      </div>
    );

  return (
    <div className="space-y-6 pt-4">
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Numeração e Ambiente</CardTitle>
          <CardDescription>NFC-e (modelo 65) — venda ao consumidor com QR Code.</CardDescription>
        </CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="space-y-1">
            <Label>Próximo Número</Label>
            <Input type="number" min={1} value={form.numNfce}
              onChange={(e) => set('numNfce', Number(e.target.value))} />
          </div>
          <div className="space-y-1">
            <Label>Série</Label>
            <Input value={form.serieNfce} maxLength={5}
              onChange={(e) => set('serieNfce', e.target.value)} />
          </div>
          <div className="space-y-1">
            <Label>Versão do Layout</Label>
            <Input value={form.versaoLayout ?? ''} placeholder="4.00"
              onChange={(e) => set('versaoLayout', e.target.value || null)} />
          </div>
          <div className="space-y-1">
            <Label>Ambiente</Label>
            <Select value={String(form.tipoAmbiente)}
              onValueChange={(v) => set('tipoAmbiente', Number(v))}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="2">
                  <span className="flex items-center gap-2">
                    Homologação
                    <Badge className="bg-yellow-500/10 text-yellow-600 text-xs">Testes</Badge>
                  </span>
                </SelectItem>
                <SelectItem value="1">
                  <span className="flex items-center gap-2">
                    Produção
                    <Badge className="bg-green-500/10 text-green-600 text-xs">Fiscal real</Badge>
                  </span>
                </SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label>Tipo de Emissão</Label>
            <Select value={String(form.tipoEmissao)}
              onValueChange={(v) => set('tipoEmissao', Number(v))}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="1">Normal (online)</SelectItem>
                <SelectItem value="9">Contingência offline NFC-e</SelectItem>
                <SelectItem value="6">SVC-AN</SelectItem>
                <SelectItem value="7">SVC-RS</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label>Timeout (segundos)</Label>
            <Input type="number" min={5} max={600} value={form.timeout}
              onChange={(e) => set('timeout', Number(e.target.value))} />
          </div>
        </CardContent>
      </Card>

      <Card className="border-blue-500/30">
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <FileKey className="h-4 w-4 text-blue-500" />
            CSC (Código de Segurança do Contribuinte)
          </CardTitle>
          <CardDescription>
            Cadastrado na SEFAZ do estado — usado para gerar o QR Code da NFC-e.
            Cada empresa tem um Identificador (5 dígitos) e uma Chave (até 36 caracteres).
          </CardDescription>
        </CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="space-y-1">
            <Label>Identificador (CSC ID)</Label>
            <Input value={form.identificadorCsc ?? ''} maxLength={10} placeholder="000001"
              onChange={(e) => set('identificadorCsc', e.target.value || null)} />
          </div>
          <div className="space-y-1 md:col-span-2">
            <Label>Chave CSC</Label>
            <Input type="password" value={form.csc ?? ''} maxLength={80}
              placeholder="Token cadastrado na SEFAZ"
              onChange={(e) => set('csc', e.target.value || null)} />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">QR Code</CardTitle>
          <CardDescription>Versão e posição do QR Code no DANFE simplificado.</CardDescription>
        </CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-1">
            <Label>Versão</Label>
            <Select value={form.versaoQrCode ?? '2.0'}
              onValueChange={(v) => set('versaoQrCode', v)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="2.0">2.0 (atual)</SelectItem>
                <SelectItem value="3.0">3.0</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label>Posição no cupom</Label>
            <Select value={form.posicaoQrCode ?? 'Abaixo'}
              onValueChange={(v) => set('posicaoQrCode', v)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="Abaixo">Abaixo</SelectItem>
                <SelectItem value="Lateral">Lateral</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Impressão</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-1">
              <Label>Emissão Normal</Label>
              <Select value={form.emissaoNormal ?? 'UmaLinha'}
                onValueChange={(v) => set('emissaoNormal', v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="UmaLinha">Uma linha por item</SelectItem>
                  <SelectItem value="DuasLinhas">Duas linhas por item</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label>Emissão Contingência</Label>
              <Select value={form.emissaoContingencia ?? 'UmaLinha'}
                onValueChange={(v) => set('emissaoContingencia', v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="UmaLinha">Uma linha por item</SelectItem>
                  <SelectItem value="DuasLinhas">Duas linhas por item</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium">Imprimir duas vias</p>
              <p className="text-xs text-muted-foreground">Para emissão offline em contingência.</p>
            </div>
            <Switch checked={form.ibImprimirDuasVias}
              onCheckedChange={(v) => set('ibImprimirDuasVias', v)} />
          </div>
        </CardContent>
      </Card>

      <Card className="border-amber-500/30">
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 text-amber-500" />
            Contingência Automática
          </CardTitle>
          <CardDescription>
            Detecta SEFAZ indisponível e cai automaticamente para emissão offline.
            Útil em pontos de venda onde a internet pode oscilar.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium">Ativar fallback automático</p>
              <p className="text-xs text-muted-foreground">Se a 1ª tentativa falhar/timeout, emite em contingência.</p>
            </div>
            <Switch checked={form.ibFallbackAutomatico}
              onCheckedChange={(v) => set('ibFallbackAutomatico', v)} />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-1">
              <Label>Timeout detecção (ms)</Label>
              <Input type="number" min={1000} max={60000} value={form.timeoutDeteccaoMs}
                onChange={(e) => set('timeoutDeteccaoMs', Number(e.target.value))}
                disabled={!form.ibFallbackAutomatico} />
            </div>
            <div className="space-y-1">
              <Label>Cache de falha (segundos)</Label>
              <Input type="number" min={5} max={600} value={form.cacheFalhaSegundos}
                onChange={(e) => set('cacheFalhaSegundos', Number(e.target.value))}
                disabled={!form.ibFallbackAutomatico} />
              <p className="text-xs text-muted-foreground">
                Quanto tempo lembrar que a SEFAZ caiu antes de re-testar.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Diretórios</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="space-y-1">
            <Label>Backup XML</Label>
            <Input value={form.diretorioBackupXml ?? ''} placeholder="C:\NFCe\XML"
              onChange={(e) => set('diretorioBackupXml', e.target.value || null)} />
          </div>
          <div className="space-y-1">
            <Label>Backup PDF (DANFE)</Label>
            <Input value={form.diretorioBackupPdf ?? ''} placeholder="C:\NFCe\PDF"
              onChange={(e) => set('diretorioBackupPdf', e.target.value || null)} />
          </div>
          <div className="space-y-1">
            <Label>Recibos</Label>
            <Input value={form.diretorioRecibo ?? ''} placeholder="C:\NFCe\Recibos"
              onChange={(e) => set('diretorioRecibo', e.target.value || null)} />
          </div>
          <div className="space-y-1">
            <Label>Schemas XSD</Label>
            <Input value={form.diretorioSchemas ?? ''} placeholder="\schemas\NFe"
              onChange={(e) => set('diretorioSchemas', e.target.value || null)} />
          </div>
        </CardContent>
      </Card>

      <div className="flex justify-end">
        <Button onClick={() => salvar(form)} disabled={isSalvando} className="gap-2 w-full sm:w-auto">
          {isSalvando
            ? <><RefreshCw className="h-4 w-4 animate-spin" />Salvando...</>
            : <><Save className="h-4 w-4" />Salvar configuração NFC-e</>}
        </Button>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
//  Aba MDF-e
// ══════════════════════════════════════════════════════════════════════════════

function TabMdfe({ empresaId }: { empresaId: number }) {
  const { data, isLoading, salvar, isSalvando } = useConfigMdfe(empresaId);

  const [form, setForm] = useState<AtualizarConfigMdfeRequest>({
    numMdfe: 1,
    serieMdfe: '001',
    versaoLayout: '3.00',
    tipoAmbiente: 2,
    tipoEmissao: 1,
    diretorioBackupXml: null,
    diretorioSaida: null,
    diretorioSchemas: '\\schemas\\MDFe',
  });

  useEffect(() => {
    if (!data) return;
    setForm({
      numMdfe: data.numMdfe,
      serieMdfe: data.serieMdfe,
      versaoLayout: data.versaoLayout,
      tipoAmbiente: data.tipoAmbiente,
      tipoEmissao: data.tipoEmissao,
      diretorioBackupXml: data.diretorioBackupXml,
      diretorioSaida: data.diretorioSaida,
      diretorioSchemas: data.diretorioSchemas,
    });
  }, [data]);

  const set = <K extends keyof AtualizarConfigMdfeRequest>(k: K, v: AtualizarConfigMdfeRequest[K]) =>
    setForm((f) => ({ ...f, [k]: v }));

  if (isLoading)
    return (
      <div className="space-y-4 pt-4">
        {Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-9 w-full" />)}
      </div>
    );

  return (
    <div className="space-y-6 pt-4">
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Numeração e Versão</CardTitle>
          <CardDescription>Próximo número, série e versão do layout MDF-e.</CardDescription>
        </CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="space-y-1">
            <Label>Próximo Número</Label>
            <Input
              type="number"
              min={1}
              value={form.numMdfe}
              onChange={(e) => set('numMdfe', Number(e.target.value))}
            />
          </div>
          <div className="space-y-1">
            <Label>Série</Label>
            <Input
              value={form.serieMdfe}
              onChange={(e) => set('serieMdfe', e.target.value)}
              maxLength={5}
            />
          </div>
          <div className="space-y-1">
            <Label>Versão do Layout</Label>
            <Select
              value={form.versaoLayout ?? '3.00'}
              onValueChange={(v) => set('versaoLayout', v)}
            >
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="3.00">3.00 (atual)</SelectItem>
                <SelectItem value="1.00">1.00 (legado 3.1)</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Ambiente e Emissão</CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-1">
            <Label>Ambiente</Label>
            <Select
              value={String(form.tipoAmbiente)}
              onValueChange={(v) => set('tipoAmbiente', Number(v))}
            >
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="2">
                  <span className="flex items-center gap-2">
                    Homologação
                    <Badge className="bg-yellow-500/10 text-yellow-600 text-xs">Testes</Badge>
                  </span>
                </SelectItem>
                <SelectItem value="1">
                  <span className="flex items-center gap-2">
                    Produção
                    <Badge className="bg-green-500/10 text-green-600 text-xs">Fiscal real</Badge>
                  </span>
                </SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label>Tipo de Emissão</Label>
            <Select
              value={String(form.tipoEmissao)}
              onValueChange={(v) => set('tipoEmissao', Number(v))}
            >
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="1">Normal (online)</SelectItem>
                <SelectItem value="2">Contingência</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Diretórios</CardTitle>
          <CardDescription>Caminhos locais para XMLs gerados, arquivos de saída e schemas XSD.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="space-y-1">
            <Label>Backup XML</Label>
            <Input
              value={form.diretorioBackupXml ?? ''}
              onChange={(e) => set('diretorioBackupXml', e.target.value || null)}
              placeholder="C:\MDFe\XML"
            />
          </div>
          <div className="space-y-1">
            <Label>Saída (PDF / DAMDFE)</Label>
            <Input
              value={form.diretorioSaida ?? ''}
              onChange={(e) => set('diretorioSaida', e.target.value || null)}
              placeholder="C:\MDFe\PDF"
            />
          </div>
          <div className="space-y-1">
            <Label>Schemas XSD</Label>
            <Input
              value={form.diretorioSchemas ?? ''}
              onChange={(e) => set('diretorioSchemas', e.target.value || null)}
              placeholder="\schemas\MDFe"
            />
          </div>
        </CardContent>
      </Card>

      <div className="flex justify-end">
        <Button onClick={() => salvar(form)} disabled={isSalvando} className="gap-2 w-full sm:w-auto">
          {isSalvando
            ? <><RefreshCw className="h-4 w-4 animate-spin" />Salvando...</>
            : <><Save className="h-4 w-4" />Salvar configuração MDF-e</>}
        </Button>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
//  Aba CT-e / CT-e OS (forms quase idênticos — diferem só nos nomes dos campos)
// ══════════════════════════════════════════════════════════════════════════════

function TabCte({ empresaId }: { empresaId: number }) {
  const { data, isLoading, salvar, isSalvando } = useConfigCte(empresaId);

  const [form, setForm] = useState<AtualizarConfigCteRequest>({
    numCte: 1,
    serieCte: '1',
    versaoLayout: '4.00',
    tipoAmbiente: 2,
    tipoEmissao: 1,
    diretorioBackupXml: null,
    diretorioBackupPdf: null,
    diretorioRecibo: null,
    diretorioSchemas: '\\schemas\\CTe',
    timeout: 30,
  });

  useEffect(() => {
    if (!data) return;
    setForm({
      numCte: data.numCte,
      serieCte: data.serieCte,
      versaoLayout: data.versaoLayout,
      tipoAmbiente: data.tipoAmbiente,
      tipoEmissao: data.tipoEmissao,
      diretorioBackupXml: data.diretorioBackupXml,
      diretorioBackupPdf: data.diretorioBackupPdf,
      diretorioRecibo: data.diretorioRecibo,
      diretorioSchemas: data.diretorioSchemas,
      timeout: data.timeout,
    });
  }, [data]);

  return (
    <CteFormBody
      titulo="CT-e"
      isLoading={isLoading}
      isSalvando={isSalvando}
      numero={form.numCte}
      serie={form.serieCte}
      versaoLayout={form.versaoLayout}
      tipoAmbiente={form.tipoAmbiente}
      tipoEmissao={form.tipoEmissao}
      diretorioBackupXml={form.diretorioBackupXml}
      diretorioBackupPdf={form.diretorioBackupPdf}
      diretorioRecibo={form.diretorioRecibo}
      diretorioSchemas={form.diretorioSchemas}
      timeout={form.timeout}
      versoesDisponiveis={['4.00', '3.00']}
      onChange={(patch) => setForm((f) => ({
        ...f,
        ...(patch.numero !== undefined ? { numCte: patch.numero } : {}),
        ...(patch.serie !== undefined ? { serieCte: patch.serie } : {}),
        ...(patch.versaoLayout !== undefined ? { versaoLayout: patch.versaoLayout } : {}),
        ...(patch.tipoAmbiente !== undefined ? { tipoAmbiente: patch.tipoAmbiente } : {}),
        ...(patch.tipoEmissao !== undefined ? { tipoEmissao: patch.tipoEmissao } : {}),
        ...(patch.diretorioBackupXml !== undefined ? { diretorioBackupXml: patch.diretorioBackupXml } : {}),
        ...(patch.diretorioBackupPdf !== undefined ? { diretorioBackupPdf: patch.diretorioBackupPdf } : {}),
        ...(patch.diretorioRecibo !== undefined ? { diretorioRecibo: patch.diretorioRecibo } : {}),
        ...(patch.diretorioSchemas !== undefined ? { diretorioSchemas: patch.diretorioSchemas } : {}),
        ...(patch.timeout !== undefined ? { timeout: patch.timeout } : {}),
      }))}
      onSalvar={() => salvar(form)}
    />
  );
}

function TabCteOs({ empresaId }: { empresaId: number }) {
  const { data, isLoading, salvar, isSalvando } = useConfigCteOs(empresaId);

  const [form, setForm] = useState<AtualizarConfigCteOsRequest>({
    numCteOs: 1,
    serieCteOs: '1',
    versaoLayout: '3.10',
    tipoAmbiente: 2,
    tipoEmissao: 1,
    diretorioBackupXml: null,
    diretorioBackupPdf: null,
    diretorioRecibo: null,
    diretorioSchemas: '\\schemas\\CTe',
    timeout: 30,
  });

  useEffect(() => {
    if (!data) return;
    setForm({
      numCteOs: data.numCteOs,
      serieCteOs: data.serieCteOs,
      versaoLayout: data.versaoLayout,
      tipoAmbiente: data.tipoAmbiente,
      tipoEmissao: data.tipoEmissao,
      diretorioBackupXml: data.diretorioBackupXml,
      diretorioBackupPdf: data.diretorioBackupPdf,
      diretorioRecibo: data.diretorioRecibo,
      diretorioSchemas: data.diretorioSchemas,
      timeout: data.timeout,
    });
  }, [data]);

  return (
    <CteFormBody
      titulo="CT-e OS"
      isLoading={isLoading}
      isSalvando={isSalvando}
      numero={form.numCteOs}
      serie={form.serieCteOs}
      versaoLayout={form.versaoLayout}
      tipoAmbiente={form.tipoAmbiente}
      tipoEmissao={form.tipoEmissao}
      diretorioBackupXml={form.diretorioBackupXml}
      diretorioBackupPdf={form.diretorioBackupPdf}
      diretorioRecibo={form.diretorioRecibo}
      diretorioSchemas={form.diretorioSchemas}
      timeout={form.timeout}
      versoesDisponiveis={['3.10', '4.00']}
      onChange={(patch) => setForm((f) => ({
        ...f,
        ...(patch.numero !== undefined ? { numCteOs: patch.numero } : {}),
        ...(patch.serie !== undefined ? { serieCteOs: patch.serie } : {}),
        ...(patch.versaoLayout !== undefined ? { versaoLayout: patch.versaoLayout } : {}),
        ...(patch.tipoAmbiente !== undefined ? { tipoAmbiente: patch.tipoAmbiente } : {}),
        ...(patch.tipoEmissao !== undefined ? { tipoEmissao: patch.tipoEmissao } : {}),
        ...(patch.diretorioBackupXml !== undefined ? { diretorioBackupXml: patch.diretorioBackupXml } : {}),
        ...(patch.diretorioBackupPdf !== undefined ? { diretorioBackupPdf: patch.diretorioBackupPdf } : {}),
        ...(patch.diretorioRecibo !== undefined ? { diretorioRecibo: patch.diretorioRecibo } : {}),
        ...(patch.diretorioSchemas !== undefined ? { diretorioSchemas: patch.diretorioSchemas } : {}),
        ...(patch.timeout !== undefined ? { timeout: patch.timeout } : {}),
      }))}
      onSalvar={() => salvar(form)}
    />
  );
}

interface CteFormBodyProps {
  titulo: string;
  isLoading: boolean;
  isSalvando: boolean;
  numero: number;
  serie: string;
  versaoLayout: string | null;
  tipoAmbiente: number;
  tipoEmissao: number;
  diretorioBackupXml: string | null;
  diretorioBackupPdf: string | null;
  diretorioRecibo: string | null;
  diretorioSchemas: string | null;
  timeout: number;
  versoesDisponiveis: string[];
  onChange: (patch: Partial<{
    numero: number; serie: string; versaoLayout: string | null;
    tipoAmbiente: number; tipoEmissao: number;
    diretorioBackupXml: string | null; diretorioBackupPdf: string | null;
    diretorioRecibo: string | null; diretorioSchemas: string | null;
    timeout: number;
  }>) => void;
  onSalvar: () => void;
}

function CteFormBody(p: CteFormBodyProps) {
  if (p.isLoading)
    return (
      <div className="space-y-4 pt-4">
        {Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} className="h-9 w-full" />)}
      </div>
    );

  return (
    <div className="space-y-6 pt-4">
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Numeração e Versão</CardTitle>
          <CardDescription>Próximo número, série e versão do layout {p.titulo}.</CardDescription>
        </CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="space-y-1">
            <Label>Próximo Número</Label>
            <Input type="number" min={1} value={p.numero}
              onChange={(e) => p.onChange({ numero: Number(e.target.value) })} />
          </div>
          <div className="space-y-1">
            <Label>Série</Label>
            <Input value={p.serie} maxLength={5}
              onChange={(e) => p.onChange({ serie: e.target.value })} />
          </div>
          <div className="space-y-1">
            <Label>Versão do Layout</Label>
            <Select value={p.versaoLayout ?? p.versoesDisponiveis[0]}
              onValueChange={(v) => p.onChange({ versaoLayout: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {p.versoesDisponiveis.map((v) => (
                  <SelectItem key={v} value={v}>{v}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Ambiente e Emissão</CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="space-y-1">
            <Label>Ambiente</Label>
            <Select value={String(p.tipoAmbiente)}
              onValueChange={(v) => p.onChange({ tipoAmbiente: Number(v) })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="2">
                  <span className="flex items-center gap-2">
                    Homologação
                    <Badge className="bg-yellow-500/10 text-yellow-600 text-xs">Testes</Badge>
                  </span>
                </SelectItem>
                <SelectItem value="1">
                  <span className="flex items-center gap-2">
                    Produção
                    <Badge className="bg-green-500/10 text-green-600 text-xs">Fiscal real</Badge>
                  </span>
                </SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label>Tipo de Emissão</Label>
            <Select value={String(p.tipoEmissao)}
              onValueChange={(v) => p.onChange({ tipoEmissao: Number(v) })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="1">Normal (online)</SelectItem>
                <SelectItem value="2">Contingência</SelectItem>
                <SelectItem value="3">SVC-RS</SelectItem>
                <SelectItem value="4">SVC-SP</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label>Timeout (segundos)</Label>
            <Input type="number" min={5} max={600} value={p.timeout}
              onChange={(e) => p.onChange({ timeout: Number(e.target.value) })} />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Diretórios</CardTitle>
          <CardDescription>Caminhos para XMLs gerados, DACTEs, recibos e schemas XSD.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="space-y-1">
            <Label>Backup XML</Label>
            <Input value={p.diretorioBackupXml ?? ''} placeholder="C:\CTe\XML"
              onChange={(e) => p.onChange({ diretorioBackupXml: e.target.value || null })} />
          </div>
          <div className="space-y-1">
            <Label>Backup PDF (DACTE)</Label>
            <Input value={p.diretorioBackupPdf ?? ''} placeholder="C:\CTe\PDF"
              onChange={(e) => p.onChange({ diretorioBackupPdf: e.target.value || null })} />
          </div>
          <div className="space-y-1">
            <Label>Recibos</Label>
            <Input value={p.diretorioRecibo ?? ''} placeholder="C:\CTe\Recibos"
              onChange={(e) => p.onChange({ diretorioRecibo: e.target.value || null })} />
          </div>
          <div className="space-y-1">
            <Label>Schemas XSD</Label>
            <Input value={p.diretorioSchemas ?? ''} placeholder="\schemas\CTe"
              onChange={(e) => p.onChange({ diretorioSchemas: e.target.value || null })} />
          </div>
        </CardContent>
      </Card>

      <div className="flex justify-end">
        <Button onClick={p.onSalvar} disabled={p.isSalvando} className="gap-2 w-full sm:w-auto">
          {p.isSalvando
            ? <><RefreshCw className="h-4 w-4 animate-spin" />Salvando...</>
            : <><Save className="h-4 w-4" />Salvar configuração {p.titulo}</>}
        </Button>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
//  Aba Certificado
// ══════════════════════════════════════════════════════════════════════════════

function TabCertificado({ empresaId }: { empresaId: number }) {
  const { data, isLoading, salvar, isSalvando } = useConfigCertificado(empresaId);

  const [form, setForm] = useState<AtualizarConfigCertificadoRequest>({
    tipoCertificado: 2,
    nomeCertificado: null,
    serialCertificado: null,
    senhaCertificado: null,
    dtValidadeCertificado: null,
    validadeCertificado: null,
  });

  useEffect(() => {
    if (!data) return;
    setForm({
      tipoCertificado: data.tipoCertificado,
      nomeCertificado: data.nomeCertificado,
      serialCertificado: data.serialCertificado,
      senhaCertificado: data.senhaCertificado,
      dtValidadeCertificado: data.dtValidadeCertificado,
      validadeCertificado: data.validadeCertificado,
    });
  }, [data]);

  const set = <K extends keyof AtualizarConfigCertificadoRequest>(
    k: K,
    v: AtualizarConfigCertificadoRequest[K],
  ) => setForm((f) => ({ ...f, [k]: v }));

  const validadeFormatada = data?.dtValidadeCertificado
    ? new Date(data.dtValidadeCertificado).toLocaleDateString('pt-BR')
    : null;

  const isExpirado = data?.dtValidadeCertificado
    ? new Date(data.dtValidadeCertificado) < new Date()
    : false;

  if (isLoading)
    return (
      <div className="space-y-4 pt-4">
        {Array.from({ length: 5 }).map((_, i) => (
          <Skeleton key={i} className="h-9 w-full" />
        ))}
      </div>
    );

  return (
    <div className="space-y-6 pt-4">
      {data && (
        <Card className={isExpirado ? 'border-destructive/50' : 'border-green-500/30'}>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <ShieldCheck className={`h-4 w-4 ${isExpirado ? 'text-destructive' : 'text-green-500'}`} />
              Certificado Atual
            </CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
            <div>
              <p className="text-xs text-muted-foreground">Tipo</p>
              <p className="font-medium">{TIPO_CERTIFICADO_LABELS[data.tipoCertificado] ?? `Tipo ${data.tipoCertificado}`}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Nome / Caminho</p>
              <p className="font-medium truncate">{data.nomeCertificado ?? '—'}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Validade</p>
              <div className="flex items-center gap-2">
                <p className="font-medium">{validadeFormatada ?? '—'}</p>
                {validadeFormatada && (
                  <Badge className={isExpirado
                    ? 'bg-red-500/10 text-red-600'
                    : 'bg-green-500/10 text-green-600'}>
                    {isExpirado ? 'Expirado' : 'Válido'}
                  </Badge>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Atualizar Certificado</CardTitle>
          <CardDescription>
            Configure o certificado digital A1/A3 usado para assinar os documentos fiscais.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-1">
            <Label>Tipo de Certificado</Label>
            <Select
              value={String(form.tipoCertificado)}
              onValueChange={(v) => set('tipoCertificado', Number(v))}
            >
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {Object.entries(TIPO_CERTIFICADO_LABELS).map(([val, label]) => (
                  <SelectItem key={val} value={val}>{label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1">
            <Label>
              {form.tipoCertificado === 1
                ? 'Nome / Serial no repositório Windows'
                : form.tipoCertificado === 3
                ? 'Nome do token A3'
                : 'Caminho do arquivo PFX'}
            </Label>
            <Input
              value={form.nomeCertificado ?? ''}
              onChange={(e) => set('nomeCertificado', e.target.value || null)}
              placeholder={form.tipoCertificado === 2 ? 'C:\\certificados\\empresa.pfx' : ''}
            />
          </div>

          {form.tipoCertificado !== 1 && (
            <div className="space-y-1">
              <Label>Serial / Identificador</Label>
              <Input
                value={form.serialCertificado ?? ''}
                onChange={(e) => set('serialCertificado', e.target.value || null)}
              />
            </div>
          )}

          <div className="space-y-1">
            <Label>Senha do Certificado</Label>
            <Input
              type="password"
              value={form.senhaCertificado ?? ''}
              onChange={(e) => set('senhaCertificado', e.target.value || null)}
              placeholder="Em branco = manter a senha já gravada"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-1">
              <Label>Validade (data)</Label>
              <Input
                type="date"
                value={form.dtValidadeCertificado?.slice(0, 10) ?? ''}
                onChange={(e) =>
                  set('dtValidadeCertificado', e.target.value ? e.target.value : null)
                }
              />
            </div>
            <div className="space-y-1">
              <Label>Validade (texto)</Label>
              <Input
                value={form.validadeCertificado ?? ''}
                onChange={(e) => set('validadeCertificado', e.target.value || null)}
                placeholder="ex: 31/12/2026"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="flex justify-end">
        <Button onClick={() => salvar(form)} disabled={isSalvando} className="gap-2 w-full sm:w-auto">
          {isSalvando
            ? <><RefreshCw className="h-4 w-4 animate-spin" />Salvando...</>
            : <><Save className="h-4 w-4" />Salvar Certificado</>}
        </Button>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
//  Placeholder genérico para documentos ainda não implementados
// ══════════════════════════════════════════════════════════════════════════════

function TabEmDesenvolvimento({ titulo, descricao }: { titulo: string; descricao: string }) {
  return (
    <div className="pt-4">
      <Card className="border-dashed">
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Construction className="h-4 w-4 text-amber-500" />
            {titulo} — Em desenvolvimento
          </CardTitle>
          <CardDescription>{descricao}</CardDescription>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">
            Esta aba será habilitada quando o módulo de emissão correspondente for liberado.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
//  Aba Reforma Tributária (EC 132 / NT 2025.002 — destaque IBS/CBS)
// ══════════════════════════════════════════════════════════════════════════════

function TabReforma({ empresaId }: { empresaId: number }) {
  const { data, isLoading, salvar, isSalvando } = useConfigReforma(empresaId);
  const cClassTribList = useCClassTribCombo().data ?? [];

  const [form, setForm] = useState<AtualizarConfigReformaRequest>({
    destacarIbsCbs: true,
    dtInicioDestaqueIbsCbs: null,
    cClassTribPadraoId: null,
  });

  useEffect(() => {
    if (data) setForm({
      destacarIbsCbs: data.destacarIbsCbs,
      dtInicioDestaqueIbsCbs: data.dtInicioDestaqueIbsCbs,
      cClassTribPadraoId: data.cClassTribPadraoId,
    });
  }, [data]);

  if (isLoading) {
    return (
      <div className="pt-4 space-y-3">
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-48 w-full" />
      </div>
    );
  }

  return (
    <div className="pt-4 space-y-4">
      <Alert>
        <Info className="h-4 w-4" />
        <AlertTitle>Ano-teste 2026 (LC 214/2025, art. 348)</AlertTitle>
        <AlertDescription>
          CBS 0,9% + IBS 0,1% destacados por item (grupos da NT 2025.002). O destaque cumpre a
          obrigação — não há recolhimento em 2026. Homologação exige os grupos desde 01/07/2026;
          em <strong>produção, NF-e/NFC-e de empresa do Regime Normal (CRT 3) sem IBS/CBS são
          rejeitadas (cStat 1115) a partir de 03/08/2026</strong>. Empresas do Simples Nacional
          só destacam a partir de 04/01/2027 (o sistema aplica o destaque apenas para CRT 3).
        </AlertDescription>
      </Alert>

      {!form.destacarIbsCbs && (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>Destaque desligado</AlertTitle>
          <AlertDescription>
            Com o destaque desligado, notas de empresa CRT 3 serão rejeitadas pela SEFAZ a
            partir de 03/08/2026. Desligue apenas por orientação do contador.
          </AlertDescription>
        </Alert>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Landmark className="h-4 w-4 text-primary" /> Destaque de IBS/CBS
          </CardTitle>
          <CardDescription>
            Controla o preenchimento dos grupos IBS/CBS nas NF-e e NFC-e emitidas por esta empresa.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="flex items-center justify-between rounded-lg border p-4">
            <div className="space-y-0.5">
              <Label htmlFor="destacar-ibscbs">Destacar IBS/CBS nas NF-e/NFC-e</Label>
              <p className="text-sm text-muted-foreground">
                Aplicado quando a empresa é CRT 3 e a emissão é a partir da vigência abaixo.
              </p>
            </div>
            <Switch
              id="destacar-ibscbs"
              checked={form.destacarIbsCbs}
              onCheckedChange={(v) => setForm((f) => ({ ...f, destacarIbsCbs: v }))}
            />
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="dt-inicio-ibscbs">Início da vigência do destaque</Label>
              <Input
                id="dt-inicio-ibscbs"
                type="date"
                value={form.dtInicioDestaqueIbsCbs?.slice(0, 10) ?? ''}
                placeholder="2026-01-01"
                onChange={(e) => setForm((f) => ({
                  ...f,
                  dtInicioDestaqueIbsCbs: e.target.value ? e.target.value : null,
                }))}
              />
              <p className="text-xs text-muted-foreground">
                Em branco = 01/01/2026 (início do ano-teste).
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="cclass-padrao">cClassTrib padrão (produto sem classificação)</Label>
              <Select
                value={form.cClassTribPadraoId ? String(form.cClassTribPadraoId) : 'default'}
                onValueChange={(v) => setForm((f) => ({
                  ...f,
                  cClassTribPadraoId: v === 'default' ? null : Number(v),
                }))}
              >
                <SelectTrigger id="cclass-padrao">
                  <SelectValue placeholder="000001 — Tributação integral (padrão)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="default">000001 — Tributação integral (padrão)</SelectItem>
                  {cClassTribList.map((c) => (
                    <SelectItem key={c.id} value={String(c.id)}>
                      {c.codCClassTrib} — {c.nome}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                Usado quando o produto não tem cClassTrib no cadastro (Cadastros → Produto → aba fiscal).
              </p>
            </div>
          </div>

          <div className="flex justify-end">
            <Button onClick={() => salvar(form)} disabled={isSalvando} className="gap-2">
              <Save className="h-4 w-4" />
              {isSalvando ? 'Salvando…' : 'Salvar configurações'}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
//  Página
// ══════════════════════════════════════════════════════════════════════════════

const ConfiguracoesFiscais = () => {
  const { user } = useAuth();
  const empresaId = user?.empresa.id;

  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-foreground flex items-center gap-2">
          <Receipt className="h-7 w-7 text-primary" /> Documentos Fiscais
        </h1>
        <p className="text-muted-foreground mt-1">
          Configurações de emissão dos documentos fiscais eletrônicos: NF-e, NFC-e, NFS-e, CT-e, CT-e OS e MDF-e.
        </p>
      </div>

      <Tabs defaultValue="emitente">
        <TabsList className="flex-wrap h-auto">
          <TabsTrigger value="emitente" className="gap-2">
            <FileKey className="h-4 w-4" /> Emitente
          </TabsTrigger>
          <TabsTrigger value="nfe" className="gap-2">
            <FileText className="h-4 w-4" /> NF-e
          </TabsTrigger>
          <TabsTrigger value="nfce" className="gap-2">
            <Receipt className="h-4 w-4" /> NFC-e
          </TabsTrigger>
          <TabsTrigger value="reforma" className="gap-2">
            <Landmark className="h-4 w-4" /> Reforma Tributária
          </TabsTrigger>
          <TabsTrigger value="nfse" className="gap-2">
            <FileBarChart className="h-4 w-4" /> NFS-e
          </TabsTrigger>
          <TabsTrigger value="cte" className="gap-2">
            <Truck className="h-4 w-4" /> CT-e
          </TabsTrigger>
          <TabsTrigger value="cteos" className="gap-2">
            <Truck className="h-4 w-4" /> CT-e OS
          </TabsTrigger>
          <TabsTrigger value="mdfe" className="gap-2">
            <Truck className="h-4 w-4" /> MDF-e
          </TabsTrigger>
          <TabsTrigger value="ibpt" className="gap-2">
            <Percent className="h-4 w-4" /> IBPT
          </TabsTrigger>
          <TabsTrigger value="certificado" className="gap-2">
            <ShieldCheck className="h-4 w-4" /> Certificado Digital
          </TabsTrigger>
        </TabsList>

        <TabsContent value="ibpt">
          <IbptTab />
        </TabsContent>

        <TabsContent value="emitente">
          {empresaId
            ? <EmitenteFiscalTab empresaId={empresaId} />
            : <p className="text-sm text-muted-foreground pt-4">Empresa não identificada.</p>}
        </TabsContent>

        <TabsContent value="nfe">
          <Alert className="mt-4">
            <AlertTitle>O que desta aba vale para a emissão</AlertTitle>
            <AlertDescription>
              Só o <strong>Próximo número</strong> (com a série) é usado: ele define de onde a numeração de produção
              continua. Ambiente, certificado e dados do emitente são os da aba <strong>Emitente</strong>; os demais
              campos desta aba não afetam a NF-e emitida.
            </AlertDescription>
          </Alert>
          {empresaId
            ? <TabNfe empresaId={empresaId} modeloFixo={55} />
            : <p className="text-sm text-muted-foreground pt-4">Empresa não identificada.</p>}
        </TabsContent>

        <TabsContent value="nfce">
          {empresaId
            ? <TabNfce empresaId={empresaId} />
            : <p className="text-sm text-muted-foreground pt-4">Empresa não identificada.</p>}
        </TabsContent>

        <TabsContent value="reforma">
          {empresaId
            ? <TabReforma empresaId={empresaId} />
            : <p className="text-sm text-muted-foreground pt-4">Empresa não identificada.</p>}
        </TabsContent>

        <TabsContent value="nfse">
          <div className="pt-4">
            {empresaId
              ? <NfseConfigForm empresaId={empresaId} />
              : <p className="text-sm text-muted-foreground">Empresa não identificada.</p>}
          </div>
        </TabsContent>

        <TabsContent value="cte">
          {empresaId
            ? <TabCte empresaId={empresaId} />
            : <p className="text-sm text-muted-foreground pt-4">Empresa não identificada.</p>}
        </TabsContent>

        <TabsContent value="cteos">
          {empresaId
            ? <TabCteOs empresaId={empresaId} />
            : <p className="text-sm text-muted-foreground pt-4">Empresa não identificada.</p>}
        </TabsContent>

        <TabsContent value="mdfe">
          {empresaId
            ? <TabMdfe empresaId={empresaId} />
            : <p className="text-sm text-muted-foreground pt-4">Empresa não identificada.</p>}
        </TabsContent>

        <TabsContent value="certificado">
          <Alert className="mt-4">
            <AlertTitle>Este certificado não é o usado na emissão</AlertTitle>
            <AlertDescription>
              O certificado A1 que assina NF-e e NFC-e é instalado na aba <strong>Emitente</strong>.
              O que for gravado aqui não muda a emissão.
            </AlertDescription>
          </Alert>
          {empresaId
            ? <TabCertificado empresaId={empresaId} />
            : <p className="text-sm text-muted-foreground pt-4">Empresa não identificada.</p>}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default ConfiguracoesFiscais;
